import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../../services/api'; 

// ---------------------------------------------------------------------
// THEME & DESIGN SYSTEM GERAL
// ---------------------------------------------------------------------
const THEME = {
  bgBase: '#F8FAFD',       
  bgSurface: '#FFFFFF',    
  textMain: '#202124',     
  textMuted: '#5F6368',    
  border: '#DADCE0',       
  shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
};

// Cores de fallback caso a empresa não tenha corPrimaria no banco
const STORE_COLORS = [
  { main: '#1A73E8', bg: '#E8F0FE' }, // Azul
  { main: '#1E8E3E', bg: '#E6F4EA' }, // Verde
  { main: '#D93025', bg: '#FCE8E6' }, // Vermelho
  { main: '#F29900', bg: '#FEF7E0' }, // Amarelo
];

// ---------------------------------------------------------------------
// UTILIDADES E FORMATAÇÃO
// ---------------------------------------------------------------------
const formatCurrency = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const getInitials = (name) => {
  if (!name || name === 'Não Informado') return 'NI';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const safeId = (str) => str.replace(/[^a-zA-Z0-9]/g, '_');

// ---------------------------------------------------------------------
// INTELIGÊNCIA DE MAPEAMENTO (USANDO A "UNIDADE" E "CODIGOLOJA")
// ---------------------------------------------------------------------

/**
 * Pega a string gravada na Ordem de Serviço (os.loja) e encontra a Empresa 
 * Oficial cruzando com codigoLoja ou unidade.
 */
const descobrirEmpresaOficial = (osLojaString, empresasCadastradas) => {
  if (!osLojaString) return null;
  const busca = osLojaString.toUpperCase().trim();

  // 1. Tenta bater exatamente com o "codigoLoja" (Ex: "V01", "V02")
  let empresaEncontrada = empresasCadastradas.find(emp => 
    emp.codigoLoja && emp.codigoLoja.toUpperCase() === busca
  );
  if (empresaEncontrada) return empresaEncontrada;

  // 2. Tenta bater exatamente com a "unidade"
  empresaEncontrada = empresasCadastradas.find(emp => 
    emp.unidade && emp.unidade.toUpperCase() === busca
  );
  if (empresaEncontrada) return empresaEncontrada;

  // 3. RETROCOMPATIBILIDADE: VIZUM 01
  if (busca.includes('V01') || busca.includes('VIZUM 01')) {
    return empresasCadastradas.find(emp => 
      (emp.codigoLoja && emp.codigoLoja.toUpperCase().includes('V01')) || 
      (emp.unidade && emp.unidade.toUpperCase().includes('V01')) ||
      (emp.unidade && emp.unidade.toUpperCase().includes('VIZUM 01'))
    ) || null;
  }

  // 4. RETROCOMPATIBILIDADE: VIZUM 02
  if (busca.includes('V02') || busca.includes('VIZUM 02')) {
    return empresasCadastradas.find(emp => 
      (emp.codigoLoja && emp.codigoLoja.toUpperCase().includes('V02')) || 
      (emp.unidade && emp.unidade.toUpperCase().includes('V02')) ||
      (emp.unidade && emp.unidade.toUpperCase().includes('VIZUM 02'))
    ) || null;
  }

  return null; 
};

/**
 * Agrupa os vendedores duplicados
 */
const normalizarNomeVendedor = (nomeBruto) => {
  if (!nomeBruto) return 'Não Informado';
  const n = nomeBruto.toUpperCase().trim();

  if (n.includes('VITOR AFONSO') || n === 'VITOR') return 'Vitor Afonso';
  if (n.includes('ANA BEATRIZ') || n.includes('ANNA BEATRIZ')) return 'Anna Beatriz';
  if (n.includes('ROSILENE')) return 'Rosilene';

  return nomeBruto.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const Icons = {
  Calendar: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
  Store: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="9" y1="22" x2="9" y2="22"></line><line x1="15" y1="22" x2="15" y2="22"></line><path d="M9 6h6"></path><path d="M9 10h6"></path><path d="M9 14h6"></path><path d="M9 18h6"></path></svg>,
  TrendingUp: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><polyline points="22 7 13 16 9 12 2 19"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>,
  User: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
  Trophy: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>,
  Pulse: () => <span className="live-pulse"></span>,
};

// ---------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// ---------------------------------------------------------------------
export default function ComparativoLojas() {
  const [ordens, setOrdens] = useState([]);
  const [empresasApi, setEmpresasApi] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const firstDayCurMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastDayCurMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [dataInicio, setDataInicio] = useState(firstDayCurMonth);
  const [dataFim, setDataFim] = useState(lastDayCurMonth);
  const [activePreset, setActivePreset] = useState('month');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resOrdens, resEmpresas] = await Promise.all([
          api.get('/ordens-servico').catch(() => ({ data: [] })),
          api.get('/empresas').catch(() => ({ data: [] }))
        ]);
        
        setOrdens(Array.isArray(resOrdens.data) ? resOrdens.data : []);
        const empresasAtivas = Array.isArray(resEmpresas.data) 
          ? resEmpresas.data.filter(e => e.ativo !== false) 
          : [];
        setEmpresasApi(empresasAtivas);
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const applyPreset = useCallback((type) => {
    const now = new Date();
    let start, end;
    switch (type) {
      case 'today': start = end = now; break;
      case 'week': {
        const day = now.getDay(); 
        const diffToMon = day === 0 ? -6 : 1 - day;
        start = new Date(now); start.setDate(now.getDate() + diffToMon); end = now;
        break;
      }
      case 'month': start = new Date(now.getFullYear(), now.getMonth(), 1); end = now; break;
      case 'lastMonth': start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
      default: start = new Date(firstDayCurMonth); end = new Date(lastDayCurMonth);
    }
    setDataInicio(start.toISOString().split('T')[0]);
    setDataFim(end.toISOString().split('T')[0]);
    setActivePreset(type);
  }, [firstDayCurMonth, lastDayCurMonth]);

  const handleManualDateChange = (setter) => (e) => {
    setter(e.target.value);
    setActivePreset(null);
  };

  // -------------------------------------------------------------------
  // MOTOR DE CÁLCULO
  // -------------------------------------------------------------------
  const stats = useMemo(() => {
    const lojasInfo = {};
    const vendedores = {};
    const dias = {};
    
    // PASSO 1: A BASE SÃO AS EMPRESAS CADASTRADAS (SEPARADAS POR ID)
    empresasApi.forEach((emp, index) => {
      // Usa a Unidade para exibição, com fallback para codigoLoja
      const nomeExibicao = emp.unidade || emp.codigoLoja || emp.nome || 'Sem Nome';
      
      lojasInfo[emp._id] = {
        _id: emp._id,
        nomeExibicao: nomeExibicao,
        codigoLoja: emp.codigoLoja,
        bruto: 0, 
        osQtd: 0, 
        osValidasQtd: 0,
        colors: {
           main: emp.corPrimaria && emp.corPrimaria !== '#0f172a' ? emp.corPrimaria : STORE_COLORS[index % STORE_COLORS.length].main,
           bg: STORE_COLORS[index % STORE_COLORS.length].bg
        }
      };
    });

    let totalValidContracts = 0;
    let totalBrutoGeral = 0;

    // PASSO 2: PROCESSAR AS ORDENS 
    ordens.forEach((os) => {
      if (!os.dataCompra) return;
      const dataOS = os.dataCompra.split('T')[0];
      if (dataOS < dataInicio || dataOS > dataFim) return;

      const empresaAlvo = descobrirEmpresaOficial(os.loja, empresasApi);
      if (!empresaAlvo) return; // Ignora se não achou loja

      const targetId = empresaAlvo._id;
      const valorBruto = Number(os.valorTotal) || 0;

      // Soma na loja correta usando o _id do banco para não misturar!
      lojasInfo[targetId].bruto += valorBruto;
      lojasInfo[targetId].osQtd += 1;
      
      if (valorBruto > 0) {
         lojasInfo[targetId].osValidasQtd += 1;
         totalValidContracts++;
      }
      totalBrutoGeral += valorBruto;

      // Agrupa para o Gráfico de Linhas usando o Nome de Exibição
      const nomeVisual = lojasInfo[targetId].nomeExibicao;
      if (!dias[dataOS]) dias[dataOS] = { dataOriginal: dataOS };
      if (!dias[dataOS][nomeVisual]) dias[dataOS][nomeVisual] = 0;
      dias[dataOS][nomeVisual] += valorBruto;

      // Processa o Vendedor
      const nomeDoVendedorBruto = os.vendedor?.nome || os.vendedor;
      const vendedorNormalizado = normalizarNomeVendedor(nomeDoVendedorBruto);

      if (!vendedores[vendedorNormalizado]) {
        vendedores[vendedorNormalizado] = { nome: vendedorNormalizado, lojaVisual: nomeVisual, bruto: 0, os: 0 };
      }
      vendedores[vendedorNormalizado].bruto += valorBruto;
      vendedores[vendedorNormalizado].os += 1;
    });

    const arrayLojas = Object.values(lojasInfo).sort((a, b) => b.bruto - a.bruto);
    const lojaLiderId = arrayLojas.length > 0 && arrayLojas[0].bruto > 0 ? arrayLojas[0]._id : null;

    arrayLojas.forEach(loja => {
       loja.share = totalBrutoGeral > 0 ? (loja.bruto / totalBrutoGeral) * 100 : 0;
    });

    const rankingVendedores = Object.values(vendedores).sort((a, b) => b.bruto - a.bruto).slice(0, 8);
    
    const chartData = Object.values(dias).sort((a, b) => new Date(a.dataOriginal) - new Date(b.dataOriginal)).map((d) => {
      const [, mes, dia] = d.dataOriginal.split('-');
      const chartPoint = { ...d, dataFormatada: `${dia}/${mes}` };
      arrayLojas.forEach(loja => { 
        if(!chartPoint[loja.nomeExibicao]) chartPoint[loja.nomeExibicao] = 0; 
      });
      return chartPoint;
    });

    return { lojas: arrayLojas, totalBrutoGeral, totalValidContracts, rankingVendedores, chartData, lojaLiderId };
  }, [ordens, empresasApi, dataInicio, dataFim]);

  if (loading) {
    return (
      <div style={{ height: '100dvh', display: 'grid', placeItems: 'center', background: THEME.bgBase }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div className="loader-spinner"></div>
          <span style={{ fontSize: 13, color: THEME.textMuted, fontWeight: 500, letterSpacing: 0.5 }}>Analisando Unidades...</span>
        </div>
      </div>
    );
  }

  if (empresasApi.length === 0) {
    return (
      <div className="analytics-container" style={{ textAlign: 'center', padding: 60 }}>
        <Icons.Store />
        <h2 style={{ marginTop: 16 }}>Nenhuma Empresa Cadastrada</h2>
        <p style={{ color: THEME.textMuted }}>Cadastre as unidades (V01, V02, etc) no painel de Empresas para visualizar os relatórios.</p>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <header className="analytics-header">
        <div className="header-titles">
          <h1 className="page-title">Comparativo de Unidades</h1>
          <p className="page-subtitle"><Icons.Pulse /> Dados isolados por Unidade Operacional</p>
        </div>

        <div className="filter-wrapper">
          <div className="date-inputs">
            <div className="input-box">
              <Icons.Calendar />
              <input type="date" value={dataInicio} onChange={handleManualDateChange(setDataInicio)} />
            </div>
            <span className="separator">-</span>
            <div className="input-box">
              <Icons.Calendar />
              <input type="date" value={dataFim} onChange={handleManualDateChange(setDataFim)} />
            </div>
          </div>

          <div className="preset-filters">
            <button className={activePreset === 'today' ? 'active' : ''} onClick={() => applyPreset('today')}>Hoje</button>
            <button className={activePreset === 'week' ? 'active' : ''} onClick={() => applyPreset('week')}>Semana</button>
            <button className={activePreset === 'month' ? 'active' : ''} onClick={() => applyPreset('month')}>Mês</button>
            <button className={activePreset === 'lastMonth' ? 'active' : ''} onClick={() => applyPreset('lastMonth')}>Mês Passado</button>
          </div>
        </div>
      </header>

      <div className="top-metrics-area">
         <div className="eligible-box">
            <div className="eligible-icon"><Icons.Trophy /></div>
            <div className="eligible-texts">
               <span>Contratos Elegíveis (R$ {'>'} 0)</span>
               <strong>{stats.totalValidContracts} vendas validadas no período</strong>
            </div>
         </div>

         <div className="market-share-box">
            <div className="share-labels">
               {stats.lojas.map(loja => (
                 <span key={loja._id} style={{ color: loja.colors.main, fontWeight: 600, fontSize: 12 }}>
                   {loja.nomeExibicao} • {loja.share.toFixed(1)}%
                 </span>
               ))}
            </div>
            <div className="share-bar-track">
               {stats.lojas.map(loja => (
                 <div key={loja._id} className="share-fill-dynamic" style={{ width: `${loja.share}%`, background: loja.colors.main }}></div>
               ))}
            </div>
         </div>
      </div>

      <div className="store-cards-grid" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(280px, 1fr))` }}>
        {stats.lojas.map((loja) => {
          const isLider = stats.lojaLiderId === loja._id;
          const ticketMedio = loja.osValidasQtd > 0 ? loja.bruto / loja.osValidasQtd : 0;
          
          return (
            <div key={loja._id} className="store-card" style={{ borderTop: isLider ? `3px solid ${loja.colors.main}` : undefined }}>
              <div className="store-card-header">
                <div className="store-badge" style={{ background: loja.colors.bg, color: loja.colors.main }}>
                  <Icons.Store /> {loja.nomeExibicao}
                </div>
                {isLider && loja.bruto > 0 && (
                  <span className="leader-tag" style={{ color: loja.colors.main, background: loja.colors.bg, border: `1px solid ${loja.colors.bg}` }}>
                    <Icons.Trophy /> Líder
                  </span>
                )}
              </div>
              <div className="store-revenue">{formatCurrency(loja.bruto)}</div>
              
              <div className="store-sub-metrics">
                <div className="sub-metric-box">
                   <span>Ticket Médio</span>
                   <strong>{formatCurrency(ticketMedio)}</strong>
                </div>
                <div className="sub-metric-box">
                   <span>Válidos</span>
                   <strong>{loja.osValidasQtd}</strong>
                </div>
                <div className="sub-metric-box">
                   <span>Total OS</span>
                   <strong>{loja.osQtd}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="charts-ranking-layout">
        <div className="glass-panel chart-panel">
          <div className="panel-head">
            <h3><Icons.TrendingUp /> Evolução Diária de Receita</h3>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  {stats.lojas.map(loja => (
                    <linearGradient key={`grad-${loja._id}`} id={`color-${safeId(loja.nomeExibicao)}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={loja.colors.main} stopOpacity={0.2}/>
                      <stop offset="95%" stopColor={loja.colors.main} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} vertical={false} />
                <XAxis dataKey="dataFormatada" stroke={THEME.textMuted} fontSize={10} tickLine={false} axisLine={false} dy={10} minTickGap={20} />
                <YAxis stroke={THEME.textMuted} fontSize={10} tickFormatter={(v) => `R$${v / 1000}k`} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip 
                  contentStyle={{ backgroundColor: THEME.bgSurface, border: `1px solid ${THEME.border}`, borderRadius: '8px', boxShadow: THEME.shadowSm }} 
                  itemStyle={{ fontWeight: 600, fontSize: '12px', color: THEME.textMain }} 
                  formatter={(value) => formatCurrency(value)} 
                  labelStyle={{ color: THEME.textMuted, marginBottom: '2px', fontWeight: 500, fontSize: '10px', textTransform: 'uppercase' }} 
                />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px', fontSize: '11px', color: THEME.textMuted }} />
                
                {stats.lojas.map(loja => (
                  <Area 
                    key={loja._id} 
                    type="monotone" 
                    dataKey={loja.nomeExibicao} 
                    stroke={loja.colors.main} 
                    strokeWidth={2} 
                    fillOpacity={1} 
                    fill={`url(#color-${safeId(loja.nomeExibicao)})`} 
                    activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5 }} 
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-panel ranking-panel">
          <div className="panel-head">
            <h3><Icons.User /> Top Consultores</h3>
          </div>
          <div className="ranking-list custom-scrollbar">
            {stats.rankingVendedores.length === 0 ? (
              <div className="empty-ranking">Nenhuma venda no período.</div>
            ) : (
              stats.rankingVendedores.map((vendedor, index) => {
                const lojaDoVendedor = stats.lojas.find(l => l.nomeExibicao === vendedor.lojaVisual);
                const colors = lojaDoVendedor ? lojaDoVendedor.colors : STORE_COLORS[0];
                const totalDaLojaDele = lojaDoVendedor && lojaDoVendedor.bruto > 0 ? lojaDoVendedor.bruto : 1;
                const progresso = Math.min((vendedor.bruto / totalDaLojaDele) * 100, 100);

                return (
                  <div key={index} className="rank-row">
                    <div className="rank-pos">{index + 1}</div>
                    
                    <div className="rank-avatar" style={{ background: colors.bg, color: colors.main }}>
                      {getInitials(vendedor.nome)}
                    </div>
                    
                    <div className="rank-info">
                       <div className="rank-header-line">
                          <div className="seller-meta">
                             <span className="seller-name">{vendedor.nome}</span>
                             <span className="seller-store" style={{ background: colors.bg, color: colors.main }}>
                               {vendedor.lojaVisual}
                             </span>
                          </div>
                          <div className="seller-numbers">
                             <strong>{formatCurrency(vendedor.bruto)}</strong>
                             <span>{vendedor.os} vendas</span>
                          </div>
                       </div>
                       
                       <div className="progress-bg">
                          <div className="progress-bar-dynamic" style={{ width: `${progresso}%`, background: colors.main }}></div>
                       </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <style>{`
        .analytics-container { background: ${THEME.bgBase}; padding: 24px; color: ${THEME.textMain}; max-width: 1200px; margin: 0 auto; animation: fadeIn 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        
        .loader-spinner { width: 36px; height: 36px; border: 3px solid #E8F0FE; border-top: 3px solid #1A73E8; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

        .live-pulse { display: inline-block; width: 6px; height: 6px; background: #D93025; border-radius: 50%; margin-right: 6px; animation: pulseRed 2s infinite; }
        @keyframes pulseRed { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(217, 48, 37, 0.4); } 70% { transform: scale(1.1); box-shadow: 0 0 0 4px rgba(217, 48, 37, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(217, 48, 37, 0); } }

        .custom-scrollbar::-webkit-scrollbar { display: none; }

        .analytics-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 24px; }
        .header-titles .page-title { font-size: 22px; font-weight: 600; margin: 0 0 4px 0; color: ${THEME.textMain}; letter-spacing: -0.5px;}
        .header-titles .page-subtitle { font-size: 11.5px; color: ${THEME.textMuted}; display: flex; align-items: center; margin: 0; font-weight: 500;}

        .filter-wrapper { display: flex; flex-direction: column; gap: 10px; align-items: flex-end; }
        .date-inputs { display: flex; align-items: center; gap: 8px; background: ${THEME.bgSurface}; padding: 6px 12px; border-radius: 20px; border: 1px solid ${THEME.border}; box-shadow: ${THEME.shadowSm}; }
        .input-box { display: flex; align-items: center; gap: 6px; color: ${THEME.textMuted}; }
        .input-box input { border: none; background: transparent; outline: none; font-size: 11.5px; font-weight: 600; color: ${THEME.textMain}; cursor: pointer; }
        .separator { color: ${THEME.textMuted}; font-weight: 500; }

        .preset-filters { display: flex; gap: 6px; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .preset-filters button { background: ${THEME.bgSurface}; color: ${THEME.textMuted}; border: 1px solid ${THEME.border}; padding: 6px 14px; border-radius: 16px; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; white-space: nowrap; box-shadow: ${THEME.shadowSm}; }
        .preset-filters button:hover { background: #F1F3F4; color: ${THEME.textMain}; }
        .preset-filters button.active { background: ${THEME.textMain}; color: ${THEME.bgSurface}; border-color: ${THEME.textMain}; }

        .top-metrics-area { display: grid; grid-template-columns: 1fr 2fr; gap: 16px; margin-bottom: 24px; }
        .eligible-box { background: ${THEME.bgSurface}; padding: 16px; border-radius: 12px; border: 1px solid ${THEME.border}; display: flex; align-items: center; gap: 14px; box-shadow: ${THEME.shadowSm}; }
        .eligible-icon { width: 40px; height: 40px; background: #FEF7E0; color: #E37400; border-radius: 10px; display: grid; place-items: center; }
        .eligible-texts { display: flex; flex-direction: column; gap: 2px; }
        .eligible-texts span { font-size: 10px; font-weight: 600; color: ${THEME.textMuted}; text-transform: uppercase; letter-spacing: 0.5px;}
        .eligible-texts strong { font-size: 15px; font-weight: 600; color: ${THEME.textMain}; }

        .market-share-box { background: ${THEME.bgSurface}; padding: 16px 20px; border-radius: 12px; border: 1px solid ${THEME.border}; display: flex; flex-direction: column; justify-content: center; box-shadow: ${THEME.shadowSm}; }
        .share-labels { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 8px; }
        .share-bar-track { width: 100%; height: 8px; background: ${THEME.border}; border-radius: 4px; display: flex; overflow: hidden; }
        .share-fill-dynamic { transition: width 1s cubic-bezier(0.16, 1, 0.3, 1); }

        .store-cards-grid { display: grid; gap: 16px; margin-bottom: 24px; }
        .store-card { background: ${THEME.bgSurface}; border-radius: 12px; border: 1px solid ${THEME.border}; padding: 20px; box-shadow: ${THEME.shadowSm}; display: flex; flex-direction: column; transition: transform 0.2s; }
        .store-card:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.05); }
        
        .store-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
        .store-badge { display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 12px; padding: 6px 12px; border-radius: 12px; }
        .leader-tag { font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 6px 10px; border-radius: 8px; display: flex; align-items: center; gap: 4px; letter-spacing: 0.5px;}

        .store-revenue { font-size: 34px; font-weight: 500; letter-spacing: -1px; margin-bottom: 24px; color: ${THEME.textMain}; }

        .store-sub-metrics { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
        .sub-metric-box { padding: 10px; display: flex; flex-direction: column; gap: 4px; border-left: 2px solid #F1F3F4; }
        .sub-metric-box span { font-size: 10px; font-weight: 600; color: ${THEME.textMuted}; text-transform: uppercase; }
        .sub-metric-box strong { font-size: 15px; font-weight: 600; color: ${THEME.textMain}; }

        .charts-ranking-layout { display: grid; grid-template-columns: 1.8fr 1.2fr; gap: 16px; }
        .glass-panel { background: ${THEME.bgSurface}; border: 1px solid ${THEME.border}; border-radius: 12px; padding: 20px; box-shadow: ${THEME.shadowSm}; display: flex; flex-direction: column; }
        .panel-head { margin-bottom: 20px; }
        .panel-head h3 { margin: 0; font-size: 15px; font-weight: 600; color: ${THEME.textMain}; display: flex; align-items: center; gap: 8px; }
        
        .chart-wrapper { width: 100%; height: 280px; }

        .ranking-list { display: flex; flex-direction: column; gap: 10px; flex: 1; max-height: 280px; overflow-y: auto; padding-right: 4px;}
        .rank-row { display: flex; align-items: center; padding: 12px; border-radius: 8px; background: #FAFAFA; border: 1px solid #F1F3F4; transition: 0.2s; }
        .rank-row:hover { background: #FFFFFF; box-shadow: ${THEME.shadowSm}; border-color: ${THEME.border}; }
        
        .rank-pos { font-size: 13px; font-weight: 700; width: 24px; color: ${THEME.textMuted}; }
        .rank-avatar { width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; font-weight: 700; font-size: 12px; margin-right: 12px; flex-shrink: 0; }

        .rank-info { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .rank-header-line { display: flex; justify-content: space-between; align-items: center; }
        .seller-meta { display: flex; align-items: center; gap: 8px; }
        .seller-name { font-size: 13px; font-weight: 600; color: ${THEME.textMain}; }
        .seller-store { font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 3px 6px; border-radius: 4px;}
        
        .seller-numbers { text-align: right; display: flex; flex-direction: column; gap: 1px;}
        .seller-numbers strong { font-size: 13px; font-weight: 600; color: ${THEME.textMain}; }
        .seller-numbers span { font-size: 10px; font-weight: 500; color: ${THEME.textMuted}; }

        .progress-bg { width: 100%; height: 5px; background: #E8EAED; border-radius: 3px; overflow: hidden; }
        .progress-bar-dynamic { height: 100%; border-radius: 3px; transition: width 1s cubic-bezier(0.16, 1, 0.3, 1); }

        .empty-ranking { text-align: center; color: ${THEME.textMuted}; padding: 40px 0; font-size: 13px; font-weight: 500;}

        @media (max-width: 1024px) {
          .charts-ranking-layout { grid-template-columns: 1fr; }
          .top-metrics-area { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .analytics-container { padding: 12px; }
          .analytics-header { flex-direction: column; align-items: stretch; gap: 12px; margin-bottom: 16px;}
          .header-titles .page-title { font-size: 18px; }
          .filter-wrapper { align-items: stretch; }
          .date-inputs { justify-content: space-between; }
          
          .share-labels { flex-direction: column; gap: 8px; align-items: flex-start; }
          .store-cards-grid { grid-template-columns: 1fr !important; gap: 12px; }
          .store-card { padding: 16px; }
          .store-revenue { font-size: 28px; margin-bottom: 16px;}
          
          .store-sub-metrics { grid-template-columns: 1fr 1fr; }
          .sub-metric-box { border-left: none; border-top: 1px solid ${THEME.border}; padding: 8px 0 0 0; margin-top: 8px;}
          .sub-metric-box:last-child { grid-column: 1 / -1; } 

          .glass-panel { padding: 16px; }
          .chart-wrapper { height: 220px; }
        }
      `}</style>
    </div>
  );
}